// /schedule — the content calendar. Phase 1 scope: see your scheduled
// posts laid out as a calendar month, schedule any approved post on
// any platform at any hour, edit/delete tiles. NO actual posting yet —
// tiles sit in status='pending' until the Phase 3 publish worker exists.
//
// Layout: month-switcher header + 7×5/6 grid (Sun–Sat). Each day cell
// shows up to N tile chips + an empty-day click target. Click a day →
// schedule modal opens with that day pre-filled. Click a tile → edit
// modal.
//
// Source of truth: scheduled_posts table (one row per tile). Posts come
// from the existing `posts` library (filtered to approved=true) — same
// data My Creatives reads.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import {
  type ScheduledPost,
  listScheduledPosts,
  deleteScheduledPost,
  clearFutureScheduled,
} from '@/lib/api';
import { listPosts, type Post, getSignedImageUrls } from '@/lib/gallery';
import ScheduleModal from './components/ScheduleModal';
import MonthGrid, { type DayCell } from './components/MonthGrid';
import AgendaList from './components/AgendaList';
import { useConfirm } from '@/hooks/useConfirm';
import { useBrand } from '@/hooks/useBrand';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';

export default function SchedulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const { activeBrand } = useBrand();
  // FB/IG connection status — fed to the ScheduleModal so its platform
  // chips render the same connected/needs-connection/coming-soon states
  // as the gallery hover-icon flow. Provider is mounted at app root
  // (App.tsx) so there's no extra fetch here — one cached snapshot
  // across all surfaces.
  const { status: fbStatus } = useFacebookConnection();
  // Anchor date for the visible month. Stored as the first-of-the-month
  // in user-local time so prev/next month nav is straightforward.
  const [anchor, setAnchor] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [tiles, setTiles] = useState<ScheduledPost[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  // Map post.id → signed (transformed) image URL for the tile thumbnail
  // on the calendar. Signed in one batch via getSignedImageUrls; transform
  // requests 400px @ q75 WebP (image-forward tile redesign — image is the
  // dominant element so quality matters more than for a small badge).
  const [postImageUrls, setPostImageUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // One-shot success banner. Driven by router state when arriving from
  // /schedule/auto. Cleared the moment we render so refresh doesn't
  // re-trigger it.
  type AdPilotToast = { tilesCreated: number; adsPerBatch: number; source: 'generate' | 'approved' } | null;
  const [adPilotToast, setAdPilotToast] = useState<AdPilotToast>(() => {
    const s = location.state as { adPilotJustScheduled?: AdPilotToast } | null;
    return s?.adPilotJustScheduled ?? null;
  });
  // Clear router state on first render so back/forward nav doesn't
  // re-trigger the toast. Re-fires only on a fresh /schedule/auto submit.
  const stateClearedRef = useRef(false);
  useEffect(() => {
    if (stateClearedRef.current) return;
    stateClearedRef.current = true;
    if (adPilotToast) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [adPilotToast, location.pathname, navigate]);

  // Modal state — single instance handles both "create" and "edit". When
  // editing, `editingTiles` is the cohort (every tile sharing the clicked
  // tile's post + scheduled_at); create opens with a pre-filled day from
  // the clicked cell.
  const [modalOpen, setModalOpen] = useState(false);
  const [createDay, setCreateDay] = useState<Date | null>(null);
  // Clear-future confirmation modal — destructive action so always
  // requires explicit confirm before firing.
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  // Cohort of tiles being edited (every tile sharing the clicked tile's
  // post_id + scheduled_at). Empty array = create mode. Multi-tile edit
  // mirrors the gallery's hover-icon flow so users on the calendar see
  // the same multi-platform editor.
  const [editingTiles, setEditingTiles] = useState<ScheduledPost[]>([]);

  // Window for the calendar fetch — grab 6 weeks either side of the
  // visible month so prev/next nav stays snappy.
  const fetchWindow = useMemo(() => {
    const start = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 2, 0, 23, 59, 59);
    return { start, end };
  }, [anchor]);

  const reload = useCallback(async (opts?: { background?: boolean }) => {
    if (!opts?.background) setLoading(true);
    try {
      const [t, p] = await Promise.all([
        listScheduledPosts(fetchWindow),
        listPosts(activeBrand?.id ?? null),
      ]);
      setTiles(t);
      // Only approved posts are pickable. Older posts default true so
      // this is a no-op for existing rows; future "needs approval" UX
      // can flip new saves to false without changing this filter.
      const approvedPosts = p.filter((post) => (post as Post & { approved?: boolean }).approved !== false);
      setPosts(approvedPosts);

      // Resolve a per-post image URL for the calendar tiles. Prefer the baked
      // WEBP thumbnail (small + crisp in a ~72px cell) when the post has one;
      // posts that predate the feature fall back to the full-res image (GCS
      // can't transform on the fly, so the browser downscales those). Carousel
      // zips are skipped (no single-frame thumbnail; falls back to placeholder).
      const byId: Record<string, string> = {};
      const needsFallback: string[] = [];
      for (const post of approvedPosts) {
        if (post.image_path.endsWith('.zip')) continue;
        if (post.thumbnail_url) byId[post.id] = post.thumbnail_url;
        else needsFallback.push(post.image_path);
      }
      try {
        if (needsFallback.length > 0) {
          const urlByPath = await getSignedImageUrls(needsFallback, 3600);
          for (const post of approvedPosts) {
            if (!byId[post.id] && urlByPath[post.image_path]) {
              byId[post.id] = urlByPath[post.image_path];
            }
          }
        }
        setPostImageUrls(byId);
      } catch (err) {
        // Best-effort — tiles render a placeholder when URL is missing. Keep
        // whatever thumbnails we already resolved.
        console.error('[schedule] thumb resolve failed', err);
        setPostImageUrls(byId);
      }
    } catch (err) {
      console.error('[schedule] load failed', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWindow, activeBrand?.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Are there any auto_generate tiles still waiting on the worker? While
   *  yes, we poll every 10s to surface freshly-rendered ads. This drives
   *  the Ad Pilot week-1 immediate-generation UX — user hits Schedule,
   *  lands on calendar, watches placeholders fill in over the next
   *  ~30-90s as the background task renders each ad. */
  const hasPendingAutoTiles = useMemo(
    () =>
      tiles.some(
        (t) =>
          t.mode === 'auto_generate' &&
          !t.generated_post_id &&
          (t.status === 'pending' || t.status === 'generating'),
      ),
    [tiles],
  );

  useEffect(() => {
    if (!hasPendingAutoTiles) return;
    const interval = window.setInterval(() => {
      void reload({ background: true });
    }, 10_000);
    return () => window.clearInterval(interval);
  }, [hasPendingAutoTiles, reload]);

  /** When arriving fresh from Ad Pilot, auto-jump the calendar to the
   *  month containing the earliest scheduled tile so the user actually
   *  SEES their new schedule. Without this, batches that start "next
   *  Monday" can land in a future month while the calendar opens to
   *  today's. Fires once per toast. */
  const monthJumpedRef = useRef(false);
  useEffect(() => {
    if (!adPilotToast || monthJumpedRef.current || tiles.length === 0) return;
    const newAutoTiles = tiles.filter(
      (t) => t.mode === 'auto_generate' && !t.generated_post_id,
    );
    if (newAutoTiles.length === 0) return;
    const earliest = newAutoTiles.reduce((min, t) =>
      t.scheduled_at < min.scheduled_at ? t : min,
    );
    const earliestDate = new Date(earliest.scheduled_at);
    const newAnchor = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);
    if (newAnchor.getTime() !== anchor.getTime()) {
      setAnchor(newAnchor);
    }
    monthJumpedRef.current = true;
  }, [adPilotToast, tiles, anchor]);

  // Group tiles by local-date key (YYYY-MM-DD) so MonthGrid can render
  // them per cell without re-grouping on every render.
  const tilesByDay = useMemo(() => {
    const map: Record<string, ScheduledPost[]> = {};
    for (const t of tiles) {
      const local = new Date(t.scheduled_at);
      const key = dateKey(local);
      (map[key] ||= []).push(t);
    }
    // Sort each day's tiles by time so they render top-to-bottom in
    // chronological order within a cell.
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
    }
    return map;
  }, [tiles]);

  // Build the day cells for the visible month. 6-row grid (42 cells)
  // covering the month + leading/trailing days from adjacent months —
  // standard calendar layout.
  const cells = useMemo<DayCell[]>(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const startWeekday = first.getDay(); // 0 = Sun
    // Start the grid on the Sunday of the week containing day 1.
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startWeekday);

    const cells: DayCell[] = [];
    const todayKey = dateKey(new Date());
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      const key = dateKey(d);
      cells.push({
        date: d,
        key,
        inMonth: d.getMonth() === anchor.getMonth(),
        isToday: key === todayKey,
        tiles: tilesByDay[key] || [],
      });
    }
    return cells;
  }, [anchor, tilesByDay]);

  // Posts indexed by id — shared by MonthGrid and AgendaList for fast
  // tile→post resolution. Memoised on the posts array identity.
  const postsById = useMemo(() => {
    const m: Record<string, Post> = {};
    for (const p of posts) m[p.id] = p;
    return m;
  }, [posts]);

  // Click handlers — open the modal in the right mode.
  const handleDayClick = (d: Date) => {
    setEditingTiles([]);
    setCreateDay(d);
    setModalOpen(true);
  };
  const handleTileClick = (tile: ScheduledPost) => {
    // Edit the whole COHORT — every tile in this month's window that
    // shares the clicked tile's post + scheduled_at. Lets the user
    // toggle platforms or move the whole group's time at once, matching
    // how the gallery surfaces it. Other-time tiles for the same post
    // stay on the calendar as separate cards.
    const sourceId = tile.post_id || tile.generated_post_id;
    const cohort = tiles.filter((t) => {
      const tid = t.post_id || t.generated_post_id;
      return (
        tid === sourceId &&
        t.scheduled_at === tile.scheduled_at &&
        (t.status === 'pending' || t.status === 'generating' || t.status === 'publishing')
      );
    });
    setEditingTiles(cohort.length > 0 ? cohort : [tile]);
    setCreateDay(null);
    setModalOpen(true);
  };
  const handleDeleteTile = async (tile: ScheduledPost) => {
    const ok = await confirm({
      title: 'Cancel this scheduled post?',
      message: 'The source post stays in My Creatives.',
      confirmLabel: 'Cancel post',
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    // Optimistic
    setTiles((prev) => prev.filter((t) => t.id !== tile.id));
    try {
      await deleteScheduledPost(tile.id);
    } catch (err) {
      console.error('[schedule] delete failed', err);
      void reload();
    }
  };

  const handleClearFuture = async () => {
    setClearError(null);
    setClearing(true);
    try {
      await clearFutureScheduled();
      setClearOpen(false);
      await reload();
    } catch (err) {
      console.error('[schedule] clear-future failed', err);
      setClearError(err instanceof Error ? err.message : 'Could not clear the calendar. Try again.');
    } finally {
      setClearing(false);
    }
  };

  // Month label + nav
  const monthLabel = anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const prevMonth = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const nextMonth = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
  const goToday = () => setAnchor(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  return (
    <AppLayoutV2>
      {/* Wider than the standard 1500px shell — the calendar is dense
          7-column content (each cell ~210px at 1500px) and benefits
          from extra horizontal room so post titles fit on one line.
          Lifts cells from ~210px to ~250px each (+~40px / tile) at
          viewports ≥1800px; narrower viewports remain capped by the
          window. */}
      <div className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Ad Pilot success banner — fires once when the user arrives
            from /schedule/auto. Dismisses on click or auto after 12s.
            Hidden once dismissed; router state was already cleared on
            mount so a refresh won't bring it back. */}
        {adPilotToast && (
          <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-start gap-3 animate-in fade-in duration-300">
            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-flight-takeoff-line text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-emerald-900 text-sm">
                Ad Pilot is live — {adPilotToast.tilesCreated} post{adPilotToast.tilesCreated === 1 ? '' : 's'} scheduled
              </div>
              <div className="text-xs text-emerald-800/80 mt-0.5">
                {adPilotToast.source === 'generate' ? (
                  <>
                    We're generating this week's <strong>{Math.ceil(adPilotToast.adsPerBatch / 4)}</strong>
                    {' '}ad{Math.ceil(adPilotToast.adsPerBatch / 4) === 1 ? '' : 's'} right now —
                    placeholders fill in as each render lands (refreshing automatically).
                  </>
                ) : (
                  <>Saved posts will cycle through the schedule at each platform's best time.</>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAdPilotToast(null)}
              className="text-emerald-700/70 hover:text-emerald-900 p-1 rounded-md hover:bg-emerald-100 cursor-pointer flex-shrink-0"
              aria-label="Dismiss"
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
        )}

        {/* Header — stacked layout on mobile (title block on top, then
            month nav, then Ad Pilot full-width). At sm+ everything
            sits on one row right-aligned. */}
        <div className="mb-6">
          {/* Title block */}
          <div className="mb-4 sm:mb-0 sm:flex sm:items-end sm:justify-between sm:gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-calendar-line text-primary text-2xl" />
                <h1 className="font-display font-bold text-dark text-2xl">Calendar</h1>
              </div>
              <p className="text-dark-muted text-sm">
                Schedule your approved posts. Once your social accounts are connected, posts publish automatically.
              </p>
            </div>
            {/* Controls — sm+ inline; mobile wraps below the title */}
            <div className="hidden sm:flex sm:items-center sm:gap-2 sm:flex-shrink-0">
              <MonthNav
                monthLabel={monthLabel}
                onPrev={prevMonth}
                onNext={nextMonth}
                onToday={goToday}
              />
              <button
                onClick={() => { setClearError(null); setClearOpen(true); }}
                disabled={tiles.length === 0}
                className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                title="Cancel every future scheduled post"
              >
                <i className="ri-delete-bin-line" /> Clear
              </button>
              <button
                onClick={() => navigate('/schedule/auto')}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
                title="Fill your calendar automatically with AI ads or saved posts"
              >
                <i className="ri-flight-takeoff-line" /> Ad Pilot
              </button>
            </div>
          </div>

          {/* Mobile controls — stacked below the title block. Month nav
              centered, Ad Pilot primary CTA full-width, Clear as a
              compact secondary action below it. */}
          <div className="sm:hidden flex flex-col gap-3">
            <div className="flex items-center justify-center gap-2">
              <MonthNav
                monthLabel={monthLabel}
                onPrev={prevMonth}
                onNext={nextMonth}
                onToday={goToday}
              />
            </div>
            <button
              onClick={() => navigate('/schedule/auto')}
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
              title="Fill your calendar automatically with AI ads or saved posts"
            >
              <i className="ri-flight-takeoff-line" /> Ad Pilot
            </button>
            {tiles.length > 0 && (
              <button
                onClick={() => { setClearError(null); setClearOpen(true); }}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold transition-all cursor-pointer"
              >
                <i className="ri-delete-bin-line" /> Clear all future
              </button>
            )}
          </div>
        </div>

        {/* Loading state — keep the chrome visible so users see the layout
            forming. */}
        {loading && (
          <div className="text-center py-6 text-dark-muted text-sm">
            <i className="ri-loader-4-line text-primary text-lg animate-spin mr-1" />
            Loading your calendar…
          </div>
        )}

        {/* Calendar surface — month grid on tablet/desktop, agenda list
            on mobile. 7-col grid doesn't fit phone widths; AgendaList
            shows the same data as a vertical list grouped by day. */}
        <div className="hidden md:block">
          <MonthGrid
            cells={cells}
            onDayClick={handleDayClick}
            onTileClick={handleTileClick}
            onTileDelete={handleDeleteTile}
            postsById={postsById}
            postImageUrls={postImageUrls}
          />
        </div>
        <div className="md:hidden">
          <AgendaList
            cells={cells}
            onDayClick={handleDayClick}
            onTileClick={handleTileClick}
            onTileDelete={handleDeleteTile}
            postsById={postsById}
            postImageUrls={postImageUrls}
          />
        </div>

        {/* Empty-state hint — when the user hasn't scheduled anything yet
            AND there are posts in My Creatives to schedule. */}
        {!loading && tiles.length === 0 && posts.length > 0 && (
          <div className="mt-6 rounded-2xl glass-stroke bg-white/85 px-6 py-8 text-center">
            <i className="ri-calendar-event-line text-3xl text-dark-muted/60" />
            <p className="font-display font-bold text-dark text-base mt-3">No posts scheduled yet</p>
            <p className="text-sm text-dark-muted mt-1">
              Click any day above to schedule one of your approved posts.
            </p>
          </div>
        )}
        {!loading && posts.length === 0 && (
          <div className="mt-6 rounded-2xl glass-stroke bg-white/85 px-6 py-8 text-center">
            <i className="ri-image-line text-3xl text-dark-muted/60" />
            <p className="font-display font-bold text-dark text-base mt-3">No posts ready to schedule</p>
            <p className="text-sm text-dark-muted mt-1">
              Save a post from the wizard or Ad Strategist first — it'll show up here.
            </p>
            <a
              href="/create"
              className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
            >
              <i className="ri-add-line" /> Create a post
            </a>
          </div>
        )}
      </div>

      {modalOpen && (
        <ScheduleModal
          editingTiles={editingTiles.length > 0 ? editingTiles : null}
          createDay={createDay}
          posts={posts}
          fbStatus={fbStatus}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); void reload(); }}
        />
      )}

      {/* Clear-future confirmation. Destructive — explicit copy + a
          contrasting red CTA. Closes via Escape / backdrop click /
          Cancel. */}
      {clearOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !clearing && setClearOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-md rounded-2xl glass-stroke bg-white shadow-2xl shadow-red-200/20 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-display font-bold text-dark text-lg mb-2">
              Clear all future scheduled posts?
            </h3>
            <p className="text-dark-muted text-sm mb-1">
              This will cancel every upcoming scheduled post on your calendar AND stop any active
              Ad Pilot batches.
            </p>
            <p className="text-dark-muted text-sm mb-5">
              Posts already published and source posts in My Creatives are kept.
            </p>
            {clearError && (
              <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5" />
                <div className="flex-1">{clearError}</div>
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setClearOpen(false)}
                disabled={clearing}
                className="flex-1 px-4 py-2.5 rounded-full border border-surface-dark/40 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Keep them
              </button>
              <button
                type="button"
                onClick={handleClearFuture}
                disabled={clearing}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {clearing ? (
                  <><i className="ri-loader-4-line animate-spin" /> Clearing…</>
                ) : (
                  <><i className="ri-delete-bin-line" /> Clear all</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayoutV2>
  );
}

// YYYY-MM-DD key in LOCAL time for grouping tiles by day. Using local
// (not UTC) so a tile at 10pm Wed local renders in the Wed cell, not
// the Thu cell its UTC timestamp might land in.
function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// MonthNav — reusable prev/label/next/today control cluster, used by
// both the desktop right-aligned header and the mobile stacked header.
function MonthNav({
  monthLabel,
  onPrev,
  onNext,
  onToday,
}: {
  monthLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}) {
  return (
    <>
      <button
        onClick={onPrev}
        className="w-9 h-9 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted flex items-center justify-center transition-all cursor-pointer flex-shrink-0"
        aria-label="Previous month"
      >
        <i className="ri-arrow-left-s-line text-lg" />
      </button>
      <div className="font-display font-bold text-dark text-base min-w-[140px] text-center">
        {monthLabel}
      </div>
      <button
        onClick={onNext}
        className="w-9 h-9 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted flex items-center justify-center transition-all cursor-pointer flex-shrink-0"
        aria-label="Next month"
      >
        <i className="ri-arrow-right-s-line text-lg" />
      </button>
      <button
        onClick={onToday}
        className="ml-1 px-3 py-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-xs font-bold transition-all cursor-pointer flex-shrink-0"
      >
        Today
      </button>
    </>
  );
}
